import { Injectable } from "@angular/core";
import { User } from "../models/models";
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: "root"
})
export class StorageService {
  private ENCRYPTION_KEY = undefined;
  readonly USER_STORAGE_KEY = 'user';
  readonly LOCAL_STORAGE_KEY_PREFIX = 'local_';
  private readonly SHARED_ENCRYPTION_KEY = 'shared_encryption_key';
  
  constructor() {
    // Try to restore encryption key using browser-specific info
    this.restoreEncryptionKey();

    // Listen for storage changes from other tabs
    window.addEventListener('storage', (event) => {
      if (event.key === this.SHARED_ENCRYPTION_KEY) {
        // Another tab has updated the encryption key
        this.ENCRYPTION_KEY = event.newValue;
      }
    });
  }



  restoreEncryptionKey() {
    try {
      // Try to get the shared encryption key first
      const sharedKey = localStorage.getItem(this.SHARED_ENCRYPTION_KEY);
      if (sharedKey) {
        this.ENCRYPTION_KEY = sharedKey;
        return true;
      }

      // If no shared key, try to get it from the user data
      const userData = localStorage.getItem(this.LOCAL_STORAGE_KEY_PREFIX + this.USER_STORAGE_KEY);
      if (!userData) return false;

      try {
        const user = this.decrypt(userData);
        if (!user || !user.tokenEncrypt) return false;

        // Set and share the encryption key
        this.setEncryptionKey(user.tokenEncrypt);
        return true;
      } catch (error) {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  setEncryptionKey(key: string) {
    this.ENCRYPTION_KEY = key;
    if (key) {
      // Store the encryption key in localStorage to share between tabs
      localStorage.setItem(this.SHARED_ENCRYPTION_KEY, key);
    } else {
      localStorage.removeItem(this.SHARED_ENCRYPTION_KEY);
    }
  }
  
  // Secure storage methods for sensitive data
  private encrypt(data: any): string {
    if (!this.ENCRYPTION_KEY) {
      return JSON.stringify(data);
    }
    const dataStr = JSON.stringify(data);
    return CryptoJS.AES.encrypt(dataStr, this.ENCRYPTION_KEY).toString();
  }

  private decrypt(encryptedData: string): any {
    if (!this.ENCRYPTION_KEY) {
      try {
        return JSON.parse(encryptedData);
      } catch {
        return encryptedData;
      }
    }
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.ENCRYPTION_KEY);
      const decryptedStr = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedStr);
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }



  // Local storage for non-sensitive UI preferences
  setParam(key: string, value: any): void {
    const fullKey = this.LOCAL_STORAGE_KEY_PREFIX + key;
    localStorage.setItem(fullKey, JSON.stringify(value));
  }

  getParam(key: string): any {
    const fullKey = this.LOCAL_STORAGE_KEY_PREFIX + key;
    const data = localStorage.getItem(fullKey);
    return data ? JSON.parse(data) : null;
  }

  clearParam(key: string): void {
    const fullKey = this.LOCAL_STORAGE_KEY_PREFIX + key;
    localStorage.removeItem(fullKey);
  }

  // User storage with token management
  setUserStorage(user: User) {
    if (!this.ENCRYPTION_KEY && user?.tokenEncrypt) {
      this.setEncryptionKey(user.tokenEncrypt);
    }

    // Encrypt user data
    const encryptedData = this.encrypt(user);
    if (encryptedData) {
      // Store in localStorage
      localStorage.setItem(this.LOCAL_STORAGE_KEY_PREFIX + this.USER_STORAGE_KEY, encryptedData);
    }
  }

  getUserStorage(): User | null {
    const userData = localStorage.getItem(this.LOCAL_STORAGE_KEY_PREFIX + this.USER_STORAGE_KEY);
    if (!userData) return null;

    try {
      return this.decrypt(userData);
    } catch (error) {
      console.error('Error decrypting user data:', error);
      return null;
    }
  }

  removeUserStorage() {
    // Clear shared data
    localStorage.removeItem(this.SHARED_ENCRYPTION_KEY);

    // Explicitly remove user data first (most important)
    localStorage.removeItem(this.LOCAL_STORAGE_KEY_PREFIX + this.USER_STORAGE_KEY);
    
    // Clear all other app-specific local storage items
    Object.keys(localStorage)
      .filter(key => key.startsWith(this.LOCAL_STORAGE_KEY_PREFIX))
      .forEach(key => localStorage.removeItem(key));
    
    // Reset encryption key
    this.ENCRYPTION_KEY = undefined;
  }

  getIdTab(refIdTab) {
    const user = this.getUserStorage();
    
    if (!user || !user.configs) {
      return null;
    }
    
    return user.configs[refIdTab];
  }

  
  getIdTabCustomer() {
    const user = this.getUserStorage();
    if (!user || !user.configs) {
      return null;
    }
    return user.configs['cliana'];
  }

  getToken(): string | undefined {
    try {
      const user = this.getUserStorage();
      return user?.token;
    } catch (error) {
      return undefined;
    }
  }

  getAccessToken(): string | undefined {
    try {
      const user = this.getUserStorage();
      return user?.token;
    } catch (error) {
      return undefined;
    }
  }
}